import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * @typedef {object} GroupedOptionValueInfo
 * @property {string} id - The Shopify option value ID.
 * @property {boolean} available - Whether the value is available.
 * @property {string | null} variantId - The variant ID if single-option.
 * @property {string | null} productUrl - The connected product URL.
 */

/**
 * @typedef {object} GroupedOptionMap
 * @property {number} optionPosition - The Shopify option position (1-3).
 * @property {string} optionName - The full option name (e.g., "Doors - Drawers").
 * @property {string} delimiter - The delimiter used (" - ").
 * @property {number} partCount - Number of virtual options.
 * @property {string[]} partNames - The virtual option names.
 * @property {Record<string, GroupedOptionValueInfo>} valueMap - Maps combined value text to ID info.
 */

/**
 * @typedef {object} VariantCombination
 * @property {number} id - The variant ID.
 * @property {boolean} available - Whether the variant is available.
 * @property {(string | null)[]} options - Array of option values [option1, option2, option3].
 */

/**
 * @typedef {object} OptionInfo
 * @property {number} position - The Shopify option position (1-3).
 * @property {string} name - The option name.
 * @property {boolean} isGrouped - Whether this is a grouped option.
 * @property {number} partCount - Number of parts (1 for regular, >1 for grouped).
 */

/**
 * @typedef {object} VariantCombinationsData
 * @property {string} delimiter - The delimiter used for grouped options.
 * @property {OptionInfo[]} options - Array of option info.
 * @property {VariantCombination[]} variants - Array of all variant combinations.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Map<number, GroupedOptionMap>} */
  #groupedOptionMaps = new Map();

  /** @type {VariantCombinationsData | null} */
  #variantCombinations = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    // Parse grouped option maps
    this.#parseGroupedOptionMaps();

    // Parse variant combinations for dynamic filtering
    this.#parseVariantCombinations();

    // Apply initial filtering based on default selections
    this.#filterAvailableOptions();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  /**
   * Parses the JSON data for grouped options.
   */
  #parseGroupedOptionMaps() {
    this.#groupedOptionMaps.clear();
    const mapScripts = this.querySelectorAll('script[data-grouped-option-map]');

    mapScripts.forEach((script) => {
      try {
        /** @type {GroupedOptionMap} */
        const data = JSON.parse(script.textContent || '{}');
        if (data.optionPosition) {
          this.#groupedOptionMaps.set(data.optionPosition, data);
        }
      } catch (e) {
        console.error('Failed to parse grouped option map:', e);
      }
    });
  }

  /**
   * Parses the variant combinations JSON for dynamic option filtering.
   */
  #parseVariantCombinations() {
    const script = this.querySelector('script[data-variant-combinations]');
    if (!script?.textContent) {
      this.#variantCombinations = null;
      return;
    }

    try {
      this.#variantCombinations = JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse variant combinations:', e);
      this.#variantCombinations = null;
    }
  }

  /**
   * Gets the current selections across all options.
   * Returns a map of option position -> { value, virtualParts (if grouped) }
   * @returns {Map<number, { value: string | null, virtualParts: Map<number, string> | null }>}
   */
  #getCurrentSelections() {
    /** @type {Map<number, { value: string | null, virtualParts: Map<number, string> | null }>} */
    const selections = new Map();

    if (!this.#variantCombinations) return selections;

    // Process each Shopify option position (1-3)
    for (const optionInfo of this.#variantCombinations.options) {
      const position = optionInfo.position;

      if (optionInfo.isGrouped) {
        // For grouped options, collect virtual part selections
        /** @type {Map<number, string>} */
        const virtualParts = new Map();

        for (let i = 0; i < optionInfo.partCount; i++) {
          const selector = `[data-grouped-option-position="${position}"][data-grouped-option-index="${i}"]`;
          const checkedInput = this.querySelector(`input${selector}:checked`);
          const selectedSelect = this.querySelector(`select${selector}`);

          let selectedValue = null;

          if (checkedInput instanceof HTMLInputElement) {
            selectedValue = checkedInput.dataset.groupedVirtualValue || null;
          } else if (selectedSelect instanceof HTMLSelectElement) {
            const selectedOpt = selectedSelect.options[selectedSelect.selectedIndex];
            selectedValue = selectedOpt?.dataset.groupedVirtualValue || null;
          }

          if (selectedValue) {
            virtualParts.set(i, selectedValue);
          }
        }

        selections.set(position, { value: null, virtualParts });
      } else {
        // For regular options, get the selected value
        const checkedInput = this.querySelector(
          `input[data-option-value-id]:checked`
        );
        const selectedSelect = this.querySelector(
          `select[data-option-position="${position}"] option:checked, select option[data-option-position="${position}"]:checked`
        );

        let selectedValue = null;

        // Find the checked input for this position
        const inputs = this.querySelectorAll(`input[data-option-value-id]:checked`);
        for (const input of inputs) {
          if (input instanceof HTMLInputElement) {
            // Check if this input belongs to a fieldset for this option position
            const label = input.closest('label[data-option-position]');
            if (label instanceof HTMLElement && label.dataset.optionPosition === String(position)) {
              selectedValue = input.value;
              break;
            }
          }
        }

        // Also check select dropdowns
        if (!selectedValue) {
          const options = this.querySelectorAll(`option[data-option-position="${position}"]`);
          for (const opt of options) {
            if (opt instanceof HTMLOptionElement && opt.selected) {
              selectedValue = opt.value;
              break;
            }
          }
        }

        selections.set(position, { value: selectedValue, virtualParts: null });
      }
    }

    return selections;
  }

  /**
   * Checks if a variant matches the given partial selections.
   * @param {VariantCombination} variant - The variant to check.
   * @param {Map<number, { value: string | null, virtualParts: Map<number, string> | null }>} selections - Current selections.
   * @param {number} [excludePosition] - Option position to exclude from matching.
   * @param {number} [excludeVirtualIndex] - Virtual index to exclude (for grouped options).
   * @returns {boolean} Whether the variant matches.
   */
  #variantMatchesSelections(variant, selections, excludePosition, excludeVirtualIndex) {
    if (!this.#variantCombinations) return false;

    const delimiter = this.#variantCombinations.delimiter;

    for (const [position, selection] of selections) {
      const optionIndex = position - 1; // Convert 1-indexed to 0-indexed
      const variantValue = variant.options[optionIndex];

      if (variantValue === null) continue;

      const optionInfo = this.#variantCombinations.options.find(o => o.position === position);
      if (!optionInfo) continue;

      if (optionInfo.isGrouped && selection.virtualParts) {
        // For grouped options, hold every sibling virtual part fixed at its current
        // selection. Only the specific virtual part currently being evaluated
        // (excludePosition + excludeVirtualIndex) is left unconstrained.
        //
        // This is what makes a value strike through when it would produce a
        // combination that does not exist given the user's OTHER selections within
        // the same grouped option. Cross-option correlations (e.g. Door(s)=2 only
        // pairs with Hinge=n/a) are still respected because the parts of every other
        // grouped option are also held fixed below.
        const variantParts = variantValue.split(delimiter).map(p => p.trim());

        for (const [virtualIndex, partValue] of selection.virtualParts) {
          // Skip only the exact virtual part we're currently testing.
          if (position === excludePosition && virtualIndex === excludeVirtualIndex) continue;

          if (variantParts[virtualIndex] !== partValue) {
            return false;
          }
        }
      } else if (selection.value) {
        // For regular options
        if (position === excludePosition) continue;

        if (variantValue !== selection.value) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Filters available options based on current selections.
   * Hides options that don't exist in any variant combination.
   */
  #filterAvailableOptions() {
    if (!this.#variantCombinations) return;

    const selections = this.#getCurrentSelections();
    const delimiter = this.#variantCombinations.delimiter;

    // Process each option position
    for (const optionInfo of this.#variantCombinations.options) {
      const position = optionInfo.position;

      if (optionInfo.isGrouped) {
        // For grouped options, filter each virtual part
        for (let virtualIndex = 0; virtualIndex < optionInfo.partCount; virtualIndex++) {
          // Get all unique values for this virtual position
          const labels = this.querySelectorAll(
            `label[data-grouped-option-label][data-option-position="${position}"][data-virtual-index="${virtualIndex}"]`
          );
          const options = this.querySelectorAll(
            `option[data-option-position="${position}"][data-virtual-index="${virtualIndex}"]`
          );

          const elementsToFilter = [...labels, ...options];

          for (const element of elementsToFilter) {
            if (!(element instanceof HTMLElement)) continue;

            const virtualValue = element.dataset.virtualValue;
            if (!virtualValue) continue;

            // Check if this value exists in any variant that matches current selections
            // (excluding this virtual position from the match)
            let exists = false;

            for (const variant of this.#variantCombinations.variants) {
              const variantOptionValue = variant.options[position - 1];
              if (variantOptionValue === null) continue;

              const variantParts = variantOptionValue.split(delimiter).map(p => p.trim());

              // Check if this variant has the value at this virtual position
              if (variantParts[virtualIndex] !== virtualValue) continue;

              // Check if this variant matches all other selections
              if (this.#variantMatchesSelections(variant, selections, position, virtualIndex)) {
                exists = true;
                break;
              }
            }

            // Mark as unavailable (strikethrough) if doesn't exist
            this.#updateOptionAvailability(element, exists);
          }
        }
      } else {
        // For regular options
        const labels = this.querySelectorAll(
          `label[data-option-label][data-option-position="${position}"]`
        );
        const options = this.querySelectorAll(
          `option[data-option-position="${position}"]`
        );

        const elementsToFilter = [...labels, ...options];

        for (const element of elementsToFilter) {
          if (!(element instanceof HTMLElement)) continue;

          const optionValue = element.dataset.optionValue;
          if (!optionValue) continue;

          // Check if this value exists in any variant that matches current selections
          let exists = false;

          for (const variant of this.#variantCombinations.variants) {
            const variantOptionValue = variant.options[position - 1];
            if (variantOptionValue !== optionValue) continue;

            // Check if this variant matches all other selections
            if (this.#variantMatchesSelections(variant, selections, position)) {
              exists = true;
              break;
            }
          }

          // Mark as unavailable (strikethrough) if doesn't exist
          this.#updateOptionAvailability(element, exists);
        }
      }
    }
  }

  /**
   * Updates an option element's availability state.
   * Preserves the original server-rendered availability for restoration.
   * @param {HTMLElement} element - The label or option element.
   * @param {boolean} exists - Whether this option exists for current selections.
   */
  #updateOptionAvailability(element, exists) {
    if (element instanceof HTMLLabelElement) {
      const input = element.querySelector('input');
      if (!input) return;

      // Store original availability on first encounter
      if (input.dataset.originalAvailable === undefined) {
        input.dataset.originalAvailable = input.dataset.optionAvailable || 'true';
      }

      if (!exists) {
        // Mark as non-existent (will show strikethrough but remain clickable)
        // Clicking will trigger auto-correction to find nearest valid variant
        input.dataset.optionAvailable = 'false';
        input.setAttribute('aria-disabled', 'true');
        // Note: we do NOT set input.disabled = true, so users can still click
      } else {
        // Restore to original availability (could be sold-out or available)
        input.dataset.optionAvailable = input.dataset.originalAvailable;
        if (input.dataset.originalAvailable === 'true') {
          input.removeAttribute('aria-disabled');
        }
      }
    } else if (element instanceof HTMLOptionElement) {
      // Store original text on first encounter
      if (!element.dataset.originalText) {
        element.dataset.originalText = element.textContent || '';
      }

      if (!exists) {
        // For dropdowns, keep them enabled but mark visually
        // Note: we do NOT set element.disabled = true
        const originalText = element.dataset.originalText;
        // Only add "Unavailable" if not already marked
        if (originalText && !originalText.includes(' - ')) {
          element.textContent = `${originalText} - Unavailable`;
        }
      } else {
        if (element.dataset.originalText) {
          element.textContent = element.dataset.originalText;
        }
      }
    }
  }

  /**
   * Re-initializes the radios and checked indices arrays after DOM changes (e.g., morph).
   */
  #reinitializeFieldsetState() {
    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        // Fallback: find the actually checked radio
        const checkedIndex = radios.findIndex((radio) => radio.checked);
        this.#checkedIndices.push(checkedIndex !== -1 ? [checkedIndex] : []);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    let selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Check if this is a grouped option change
    const groupedOptionPosition = selectedOption.dataset.groupedOptionPosition;
    const groupedVirtualIndex = selectedOption.dataset.groupedOptionIndex;
    const groupedVirtualValue = selectedOption.dataset.groupedVirtualValue;
    /** @type {GroupedOptionValueInfo | null} */
    let resolvedGroupedOption = null;

    // Check if the clicked option is unavailable (strikedthrough)
    // If so, we need to find a valid variant and auto-select other options
    const isUnavailable = selectedOption.dataset.optionAvailable === 'false';

    if (isUnavailable) {
      // Find the closest valid variant that includes the clicked value
      const isGrouped = !!groupedOptionPosition;
      const position = isGrouped
        ? Number(groupedOptionPosition)
        : Number(selectedOption.closest('label')?.dataset.optionPosition || 0);
      const value = isGrouped
        ? groupedVirtualValue
        : selectedOption.closest('label')?.dataset.optionValue || selectedOption.value;

      if (position && value) {
        const found = this.#findClosestVariantAndAutoSelect({
          position,
          value,
          virtualIndex: isGrouped ? Number(groupedVirtualIndex) : undefined,
          isGrouped,
        });

        if (!found) {
          console.warn('No valid variant found for clicked option');
          return;
        }
      }
    }

    if (groupedOptionPosition) {
      resolvedGroupedOption = this.#resolveGroupedOption(Number(groupedOptionPosition));
      if (!resolvedGroupedOption && groupedVirtualIndex !== undefined && groupedVirtualValue) {
        // Current combination is invalid - try to auto-correct by finding a valid combination
        // that includes the just-clicked value
        resolvedGroupedOption = this.#autoCorrectGroupedOption(
          Number(groupedOptionPosition),
          Number(groupedVirtualIndex),
          groupedVirtualValue
        );
      }
      if (!resolvedGroupedOption) {
        // Still could not resolve - no valid combination exists
        console.warn('No valid variant combination found for grouped option');
        return;
      }
    }

    this.updateSelectedOption(event.target);

    // Update available options based on new selection
    this.#filterAvailableOptions();

    this.dispatchEvent(new VariantSelectedEvent({
      id: resolvedGroupedOption?.id ?? selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = resolvedGroupedOption?.productUrl ?? selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = resolvedGroupedOption?.variantId ?? selectedOption.dataset.variantId ?? null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Resolves a grouped option by combining virtual selections into the real Shopify option value.
   * @param {number} optionPosition - The Shopify option position (1-3).
   * @returns {GroupedOptionValueInfo | null} The resolved option value info, or null if not resolvable.
   */
  #resolveGroupedOption(optionPosition) {
    const map = this.#groupedOptionMaps.get(optionPosition);
    if (!map) return null;

    // Collect selected values for each virtual option part
    const selectedParts = [];

    for (let i = 0; i < map.partCount; i++) {
      // Find the selected input/option for this part
      const selector = `[data-grouped-option-position="${optionPosition}"][data-grouped-option-index="${i}"]`;

      // Check for radio button or select
      const checkedInput = this.querySelector(`input${selector}:checked`);
      const selectedSelect = this.querySelector(`select${selector}`);

      let selectedValue = null;

      if (checkedInput instanceof HTMLInputElement) {
        selectedValue = checkedInput.dataset.groupedVirtualValue;
      } else if (selectedSelect instanceof HTMLSelectElement) {
        const selectedOpt = selectedSelect.options[selectedSelect.selectedIndex];
        selectedValue = selectedOpt?.dataset.groupedVirtualValue;
      }

      if (!selectedValue) {
        // Missing selection for this part
        return null;
      }

      selectedParts.push(selectedValue);
    }

    // Combine parts using the delimiter
    const combinedValue = selectedParts.join(map.delimiter);

    // Look up in the value map
    const valueInfo = map.valueMap[combinedValue];
    return valueInfo || null;
  }

  /**
   * Attempts to auto-correct a grouped option when the current combination is invalid.
   * Finds a valid combination that includes the just-clicked value and auto-selects
   * the corresponding values for other parts.
   * @param {number} optionPosition - The Shopify option position (1-3).
   * @param {number} clickedVirtualIndex - The virtual index that was just clicked.
   * @param {string} clickedValue - The value that was just clicked.
   * @returns {GroupedOptionValueInfo | null} The resolved option value info, or null if not resolvable.
   */
  #autoCorrectGroupedOption(optionPosition, clickedVirtualIndex, clickedValue) {
    const map = this.#groupedOptionMaps.get(optionPosition);
    if (!map) return null;

    // Find a valid combination that includes the clicked value
    // and matches current selections for other options
    const selections = this.#getCurrentSelections();
    const delimiter = this.#variantCombinations?.delimiter || ' - ';

    for (const variant of this.#variantCombinations?.variants || []) {
      const variantOptionValue = variant.options[optionPosition - 1];
      if (!variantOptionValue) continue;

      const variantParts = variantOptionValue.split(delimiter).map(p => p.trim());

      // Check if this variant has the clicked value at the clicked position
      if (variantParts[clickedVirtualIndex] !== clickedValue) continue;

      // Check if this variant matches all OTHER option positions (not this grouped option)
      let matchesOtherSelections = true;
      for (const [pos, selection] of selections) {
        if (pos === optionPosition) continue; // Skip current grouped option

        const optionIndex = pos - 1;
        const variantValue = variant.options[optionIndex];
        if (!variantValue) continue;

        const optionInfo = this.#variantCombinations?.options.find(o => o.position === pos);
        if (!optionInfo) continue;

        if (optionInfo.isGrouped && selection.virtualParts) {
          const variantPartValues = variantValue.split(delimiter).map(p => p.trim());
          for (const [virtualIndex, partValue] of selection.virtualParts) {
            if (variantPartValues[virtualIndex] !== partValue) {
              matchesOtherSelections = false;
              break;
            }
          }
        } else if (selection.value) {
          if (variantValue !== selection.value) {
            matchesOtherSelections = false;
          }
        }

        if (!matchesOtherSelections) break;
      }

      if (!matchesOtherSelections) continue;

      // Found a valid variant! Auto-select the other parts of this grouped option
      for (let i = 0; i < map.partCount; i++) {
        if (i === clickedVirtualIndex) continue; // Skip the part the user just clicked

        const partValue = variantParts[i];
        const selector = `[data-grouped-option-position="${optionPosition}"][data-grouped-option-index="${i}"]`;

        // Find and select the matching input
        const inputs = this.querySelectorAll(`input${selector}`);
        for (const input of inputs) {
          if (input instanceof HTMLInputElement && input.dataset.groupedVirtualValue === partValue) {
            input.checked = true;
            input.dataset.currentChecked = 'true';
            // Update previous checked state
            const allInputs = this.querySelectorAll(`input${selector}`);
            for (const other of allInputs) {
              if (other !== input && other instanceof HTMLInputElement) {
                other.dataset.currentChecked = 'false';
              }
            }
            break;
          }
        }

        // Also handle select dropdowns
        const select = this.querySelector(`select${selector}`);
        if (select instanceof HTMLSelectElement) {
          for (const option of select.options) {
            if (option.dataset.groupedVirtualValue === partValue) {
              select.value = option.value;
              break;
            }
          }
        }
      }

      // Now the combination should be valid - try resolving again
      return this.#resolveGroupedOption(optionPosition);
    }

    return null;
  }

  /**
   * Finds the closest valid variant that includes the clicked value and auto-selects
   * all other options to match. This is used when clicking a strikedthrough/unavailable option.
   * @param {Object} clickedInfo - Information about what was clicked.
   * @param {number} clickedInfo.position - The Shopify option position (1-3).
   * @param {string} clickedInfo.value - The value that was clicked.
   * @param {number} [clickedInfo.virtualIndex] - For grouped options, the virtual index.
   * @param {boolean} [clickedInfo.isGrouped] - Whether this is a grouped option.
   * @returns {boolean} Whether a valid variant was found and selections were updated.
   */
  #findClosestVariantAndAutoSelect(clickedInfo) {
    if (!this.#variantCombinations) return false;

    const { position, value, virtualIndex, isGrouped } = clickedInfo;
    const delimiter = this.#variantCombinations.delimiter;
    const selections = this.#getCurrentSelections();

    // Among all variants that contain the clicked value, choose the one that
    // preserves the most of the user's current selections, so clicking a
    // struck-through option changes as little as possible.
    /** @type {VariantCombination | null} */
    let bestVariant = null;
    let bestScore = -1;

    for (const variant of this.#variantCombinations.variants) {
      const variantOptionValue = variant.options[position - 1];
      if (!variantOptionValue) continue;

      let hasClickedValue = false;

      if (isGrouped && virtualIndex !== undefined) {
        // For grouped options, check the virtual part
        const parts = variantOptionValue.split(delimiter).map(p => p.trim());
        hasClickedValue = parts[virtualIndex] === value;
      } else {
        // For regular options, check the full value
        hasClickedValue = variantOptionValue === value;
      }

      if (!hasClickedValue) continue;

      const score = this.#scoreVariantAgainstSelections(variant, selections, position, virtualIndex);

      // Strictly greater keeps the first (lowest-position) variant on ties.
      if (score > bestScore) {
        bestScore = score;
        bestVariant = variant;
      }
    }

    if (!bestVariant) return false;

    // Found the closest variant with the clicked value - now auto-select all other options
    this.#autoSelectVariantOptions(bestVariant, position, virtualIndex);
    return true;
  }

  /**
   * Counts how many of the current selections a variant preserves. Used to pick the
   * "closest" variant when a user clicks a struck-through (non-existent) option.
   * @param {VariantCombination} variant - The variant to score.
   * @param {Map<number, { value: string | null, virtualParts: Map<number, string> | null }>} selections - Current selections.
   * @param {number} [excludePosition] - Option position to ignore (the clicked one).
   * @param {number} [excludeVirtualIndex] - Virtual index to ignore (for grouped options).
   * @returns {number} The number of matching option parts.
   */
  #scoreVariantAgainstSelections(variant, selections, excludePosition, excludeVirtualIndex) {
    if (!this.#variantCombinations) return 0;

    const delimiter = this.#variantCombinations.delimiter;
    let score = 0;

    for (const [position, selection] of selections) {
      const variantValue = variant.options[position - 1];
      if (variantValue == null) continue;

      const optionInfo = this.#variantCombinations.options.find(o => o.position === position);
      if (!optionInfo) continue;

      if (optionInfo.isGrouped && selection.virtualParts) {
        const variantParts = variantValue.split(delimiter).map(p => p.trim());

        for (const [virtualIndex, partValue] of selection.virtualParts) {
          if (position === excludePosition && virtualIndex === excludeVirtualIndex) continue;
          if (variantParts[virtualIndex] === partValue) score++;
        }
      } else if (selection.value) {
        if (position === excludePosition) continue;
        if (variantValue === selection.value) score++;
      }
    }

    return score;
  }

  /**
   * Auto-selects all options to match a given variant.
   * @param {VariantCombination} variant - The variant to match.
   * @param {number} [excludePosition] - Position to exclude (the one user just clicked).
   * @param {number} [excludeVirtualIndex] - Virtual index to exclude (for grouped options).
   */
  #autoSelectVariantOptions(variant, excludePosition, excludeVirtualIndex) {
    if (!this.#variantCombinations) return;

    const delimiter = this.#variantCombinations.delimiter;

    for (const optionInfo of this.#variantCombinations.options) {
      const position = optionInfo.position;
      const variantValue = variant.options[position - 1];
      if (!variantValue) continue;

      if (optionInfo.isGrouped) {
        const parts = variantValue.split(delimiter).map(p => p.trim());

        for (let virtualIndex = 0; virtualIndex < optionInfo.partCount; virtualIndex++) {
          // Skip the part the user just clicked
          if (position === excludePosition && virtualIndex === excludeVirtualIndex) continue;

          const partValue = parts[virtualIndex];
          const selector = `[data-grouped-option-position="${position}"][data-grouped-option-index="${virtualIndex}"]`;

          // Find and select the matching input
          const inputs = this.querySelectorAll(`input${selector}`);
          for (const input of inputs) {
            if (input instanceof HTMLInputElement && input.dataset.groupedVirtualValue === partValue) {
              if (!input.checked) {
                input.checked = true;
                input.dataset.currentChecked = 'true';
              }
            } else if (input instanceof HTMLInputElement) {
              input.dataset.currentChecked = 'false';
            }
          }

          // Also handle select dropdowns
          const select = this.querySelector(`select${selector}`);
          if (select instanceof HTMLSelectElement) {
            for (const option of select.options) {
              if (option.dataset.groupedVirtualValue === partValue) {
                select.value = option.value;
                break;
              }
            }
          }
        }
      } else {
        // Skip if this is the option the user just clicked
        if (position === excludePosition) continue;

        // For regular options, find and select the matching input/option
        const labels = this.querySelectorAll(`label[data-option-label][data-option-position="${position}"]`);
        for (const label of labels) {
          if (!(label instanceof HTMLElement)) continue;
          const input = label.querySelector('input');
          if (input instanceof HTMLInputElement && label.dataset.optionValue === variantValue) {
            if (!input.checked) {
              input.checked = true;
              input.dataset.currentChecked = 'true';
            }
          } else if (input instanceof HTMLInputElement) {
            input.dataset.currentChecked = 'false';
          }
        }

        // Also handle select dropdowns
        const options = this.querySelectorAll(`option[data-option-position="${position}"]`);
        for (const opt of options) {
          if (opt instanceof HTMLOptionElement && opt.dataset.optionValue === variantValue) {
            const select = opt.closest('select');
            if (select) {
              select.value = opt.value;
            }
            break;
          }
        }
      }
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Finds the array index for a fieldset based on its position in refs.fieldsets.
   * @param {HTMLFieldSetElement} fieldset - The fieldset element.
   * @returns {number} The array index, or -1 if not found.
   */
  #getFieldsetArrayIndex(fieldset) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    return fieldsets.indexOf(fieldset);
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');
      const parentFieldset = target.closest('fieldset');

      if (parentFieldset && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const arrayIndex = this.#getFieldsetArrayIndex(parentFieldset);

        if (arrayIndex === -1) {
          target.checked = true;
          return;
        }

        const fieldset = fieldsets[arrayIndex];
        const checkedIndices = this.#checkedIndices[arrayIndex];
        const radios = this.#radios[arrayIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(arrayIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"][data-variant-json]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // Re-initialize radios and checked indices after morph since DOM has changed
    this.#reinitializeFieldsetState();

    this.updateVariantPickerCss();

    // Re-parse grouped option maps after morph
    this.#parseGroupedOptionMaps();

    // Re-parse variant combinations and re-filter
    this.#parseVariantCombinations();
    this.#filterAvailableOptions();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;

    const { optionValueId, groupedOptionPosition } = selectedOption.dataset;

    // Handle grouped options
    if (groupedOptionPosition) {
      const resolved = this.#resolveGroupedOption(Number(groupedOptionPosition));
      if (resolved?.id) {
        return resolved.id;
      }
      // Fallback: return the first resolved grouped option ID we can find
      for (const position of this.#groupedOptionMaps.keys()) {
        const resolvedAny = this.#resolveGroupedOption(position);
        if (resolvedAny?.id) {
          return resolvedAny.id;
        }
      }
      return undefined;
    }

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type {string[]} */
    const result = [];

    // Track which grouped option positions we've already processed
    /** @type {Set<number>} */
    const processedGroupedPositions = new Set();

    // Process regular (non-grouped) options
    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    for (const option of selectedOptions) {
      const { optionValueId, groupedOptionPosition } = option.dataset;

      if (groupedOptionPosition) {
        // This is part of a grouped option - handle separately
        const position = Number(groupedOptionPosition);
        if (!processedGroupedPositions.has(position)) {
          processedGroupedPositions.add(position);
          const resolved = this.#resolveGroupedOption(position);
          if (resolved?.id) {
            result.push(resolved.id);
          }
        }
      } else if (optionValueId) {
        // Regular option
        result.push(optionValueId);
      }
    }

    return result;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
